import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Image,
  Input,
  List,
  Modal,
  Row,
  Segmented,
  Space,
  Spin,
  Tag,
  Typography,
  message,
  notification,
} from 'antd';
import {
  CameraOutlined,
  CheckCircleOutlined,
  PictureOutlined,
  SwapOutlined,
  SaveOutlined,
  SearchOutlined,
  UploadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import ModuleNav from './ModuleNav';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthHeadersFromStorage } from '../../utils/authHeaders';
import {
  createTryOnJob,
  createTryOnJobFromGallery,
  deleteGalleryEntry,
  getTryOnJobStatus,
  listGallery,
  listSwatchesV2,
} from '../../services/aiImageMaskingApi';

const { Title, Text, Paragraph } = Typography;
const USER_ID_KEY_BASE = 'ai_tryout_studio_new_ux_user_id';
const USER_ASSETS_KEY_BASE = 'ai_tryout_studio_new_ux_assets_by_user';
const SWATCH_PAGE_SIZE = 18;

const toAbsoluteUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url) || /^data:/i.test(url) || /^blob:/i.test(url)) {
    return url;
  }
  if (url.startsWith('local:')) {
    const relative = url.slice('local:'.length);
    const normalized = relative.startsWith('/') ? relative : `/${relative}`;
    const base = (API_BASE_URL || window.location.origin || '').replace(/\/+$/, '');
    return `${base}/uploads_v4${normalized}`;
  }
  const base = (API_BASE_URL || window.location.origin || '').replace(/\/+$/, '');
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${base}${path}`;
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Unable to read selected image'));
    reader.readAsDataURL(file);
  });

const dataUrlToFile = async (sourceUrl, filename) => {
  if (!sourceUrl) {
    throw new Error('Image source is missing');
  }

  // Handle data URLs directly to avoid browser fetch issues.
  if (/^data:/i.test(sourceUrl)) {
    const parts = sourceUrl.split(',');
    const meta = parts[0] || '';
    const body = parts[1] || '';
    const mimeMatch = meta.match(/data:([^;]+)/i);
    const mimeType = mimeMatch?.[1] || 'image/jpeg';
    const binary = atob(body);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    const extension = mimeType.includes('png') ? 'png' : mimeType.includes('webp') ? 'webp' : 'jpg';
    return new File([bytes], filename || `asset.${extension}`, { type: mimeType });
  }

  // For regular URLs:
  // - same-origin: include session/API-key headers
  // - cross-origin blob storage: use plain fetch (avoids CORS preflight failures)
  let sameOrigin = false;
  try {
    const parsed = new URL(sourceUrl, window.location.origin);
    sameOrigin = parsed.origin === window.location.origin;
  } catch (error) {
    sameOrigin = false;
  }

  let response;
  if (sameOrigin) {
    const headers = getAuthHeadersFromStorage();
    response = await fetch(sourceUrl, {
      method: 'GET',
      credentials: 'include',
      headers,
    });
  } else {
    response = await fetch(sourceUrl, {
      method: 'GET',
      mode: 'cors',
    });
  }
  if (!response.ok) {
    throw new Error(`Unable to load source image (${response.status})`);
  }
  const blob = await response.blob();
  const extension = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg';
  return new File([blob], filename || `asset.${extension}`, { type: blob.type || 'image/jpeg' });
};

const formatDuration = (durationMs) => {
  if (durationMs == null) return null;
  const seconds = Number(durationMs) / 1000;
  if (Number.isNaN(seconds)) return null;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds - minutes * 60;
  return `${minutes}m ${remaining.toFixed(1)}s`;
};

const mergeGeneratedImageMeta = (current, incoming) => {
  if (!current) return incoming;
  if (!incoming) return current;
  return {
    ...current,
    ...incoming,
    url: incoming.url || current.url,
    swatch_name: incoming.swatch_name || current.swatch_name || '',
    swatch_id: incoming.swatch_id || current.swatch_id || null,
    duration_ms:
      incoming.duration_ms != null
        ? incoming.duration_ms
        : current.duration_ms != null
        ? current.duration_ms
        : null,
    created_at: incoming.created_at || current.created_at || new Date().toISOString(),
  };
};

const USER_ID_PROMPT_TEXT = 'Set your User ID to start personal tryouts.';

const normalizeUrlForMatch = (value) => {
  if (!value) return '';
  try {
    const parsed = new URL(value, window.location.origin);
    return `${parsed.origin}${parsed.pathname}`;
  } catch (error) {
    return String(value).split('?')[0];
  }
};

const isDataUrl = (value) => /^data:/i.test(value || '');

const TryoutStudioNewUX = ({ showModuleNav = true }) => {
  const { account } = useAuth();
  const tenantId = account?.id != null ? String(account.id) : 'global';
  const userIdStorageKey = `${USER_ID_KEY_BASE}:${tenantId}`;
  const userAssetsStorageKey = `${USER_ASSETS_KEY_BASE}:${tenantId}`;
  const [systemMessage, setSystemMessage] = useState({ type: 'info', text: '' });
  const [editingUserId, setEditingUserId] = useState(false);
  const [userIdInput, setUserIdInput] = useState('');
  const [savedUserId, setSavedUserId] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [swatches, setSwatches] = useState([]);
  const [swatchLoading, setSwatchLoading] = useState(false);
  const [swatchPage, setSwatchPage] = useState(1);
  const [swatchTotal, setSwatchTotal] = useState(0);
  const [viewMode, setViewMode] = useState('grid');

  const [assetName, setAssetName] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null);
  const [savingAsset, setSavingAsset] = useState(false);
  const [assetsVersion, setAssetsVersion] = useState(0);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [submittingSwatchId, setSubmittingSwatchId] = useState(null);
  const [jobMap, setJobMap] = useState({});
  const [galleryResults, setGalleryResults] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [assetSaveModalOpen, setAssetSaveModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewImages, setPreviewImages] = useState([]);
  const [previewAssetThumb, setPreviewAssetThumb] = useState(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewFlipX, setPreviewFlipX] = useState(false);
  const [previewFlipY, setPreviewFlipY] = useState(false);
  const [previewOffset, setPreviewOffset] = useState({ x: 0, y: 0 });
  const [isPreviewDragging, setIsPreviewDragging] = useState(false);
  const [existingTryOnModalOpen, setExistingTryOnModalOpen] = useState(false);
  const [existingTryOnData, setExistingTryOnData] = useState(null);
  const [reapplyLoading, setReapplyLoading] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window === 'undefined' ? 1200 : window.innerWidth));
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const pinchStateRef = useRef({ distance: 0, zoom: 1 });
  const panStateRef = useRef({ startX: 0, startY: 0, startOffsetX: 0, startOffsetY: 0, mode: null });

  useEffect(() => {
    try {
      const cachedUserId = localStorage.getItem(userIdStorageKey) || '';
      setSavedUserId(cachedUserId);
      setUserIdInput(cachedUserId);
      setEditingUserId(!cachedUserId);
    } catch (error) {
      setEditingUserId(true);
    }
  }, [userIdStorageKey]);

  useEffect(() => {
    return () => {
      if (pendingPreview) {
        URL.revokeObjectURL(pendingPreview);
      }
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }
    };
  }, [pendingPreview]);

  useEffect(() => {
    const onResize = () => {
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const normalizedUserId = savedUserId.trim();

  const userAssets = useMemo(() => {
    if (!normalizedUserId) return [];
    // Keep cache refresh behavior tied to uploads/saves.
    void assetsVersion;
    void savingAsset;
    try {
      const raw = localStorage.getItem(userAssetsStorageKey);
      const parsed = raw ? JSON.parse(raw) : {};
      return Array.isArray(parsed[normalizedUserId]) ? parsed[normalizedUserId] : [];
    } catch (error) {
      return [];
    }
  }, [normalizedUserId, savingAsset, assetsVersion, userAssetsStorageKey]);

  const generatedDurationByUrl = useMemo(() => {
    const map = {};
    userAssets.forEach((asset) => {
      const generated = Array.isArray(asset.generated_images) ? asset.generated_images : [];
      generated.forEach((item) => {
        if (item?.url && item.duration_ms != null) {
          map[item.url] = item.duration_ms;
        }
      });
    });
    return map;
  }, [userAssets]);

  const updateCurrentUserAssets = useCallback(
    (updater) => {
      if (!normalizedUserId) return;
      const raw = localStorage.getItem(userAssetsStorageKey);
      const parsed = raw ? JSON.parse(raw) : {};
      const currentAssets = Array.isArray(parsed[normalizedUserId]) ? parsed[normalizedUserId] : [];
      const nextAssets = updater(currentAssets);
      const nextStore = { ...parsed, [normalizedUserId]: nextAssets };
      localStorage.setItem(userAssetsStorageKey, JSON.stringify(nextStore));
      setAssetsVersion((v) => v + 1);
    },
    [normalizedUserId, userAssetsStorageKey],
  );

  useEffect(() => {
    const fetchSwatches = async () => {
      setSwatchLoading(true);
      try {
        const data = await listSwatchesV2({
          page: swatchPage,
          page_size: SWATCH_PAGE_SIZE,
          search: searchTerm || undefined,
        });
        setSwatches(Array.isArray(data?.items) ? data.items : []);
        setSwatchTotal(data?.total || 0);
      } catch (error) {
        message.error(error.message || 'Unable to load swatches');
      } finally {
        setSwatchLoading(false);
      }
    };
    fetchSwatches();
  }, [searchTerm, swatchPage]);

  const refreshGalleryResults = useCallback(async () => {
    if (!normalizedUserId) {
      setGalleryResults([]);
      return;
    }
    setGalleryLoading(true);
    try {
      const data = await listGallery({ user_id: normalizedUserId, page_size: 24 });
      const items = Array.isArray(data?.items) ? data.items : [];
      setGalleryResults(
        items.map((item) => ({
          ...item,
          overlay_url: toAbsoluteUrl(item.overlay_url),
          original_url: toAbsoluteUrl(item.original_url),
        })),
      );
      // Hydrate local asset list from server gallery so assets are visible on page load.
      if (items.length) {
        const groupedByOriginal = new Map();
        items.forEach((item) => {
          const original = toAbsoluteUrl(item.original_url);
          if (!original) return;
          const key = item.lifestyle_hash || normalizeUrlForMatch(original);
          if (!groupedByOriginal.has(key)) {
            groupedByOriginal.set(key, {
              key,
              original,
              gallery_id: item.id || null,
              generated: [],
            });
          }
          const group = groupedByOriginal.get(key);
          if (!group.gallery_id && item.id) {
            group.gallery_id = item.id;
          }
          const generatedUrl = toAbsoluteUrl(item.overlay_url || item.original_url);
          if (generatedUrl) {
            group.generated.push({
              url: generatedUrl,
              swatch_name: item.swatch_name || '',
              swatch_id: item.swatch_id || null,
              duration_ms: item.duration_ms ?? null,
              created_at: item.updated_at || item.created_at || new Date().toISOString(),
            });
          }
        });

        if (groupedByOriginal.size) {
          updateCurrentUserAssets((currentAssets) => {
            const existingByHash = new Map(
              currentAssets
                .filter((asset) => asset?.source_lifestyle_hash)
                .map((asset) => [asset.source_lifestyle_hash, asset]),
            );
            const existingByNormalizedUrl = new Map(
              currentAssets
                .filter((asset) => asset?.data_url)
                .map((asset) => [normalizeUrlForMatch(asset.data_url), asset]),
            );
            const existingByGeneratedOverlayUrl = new Map();
            currentAssets.forEach((asset) => {
              const generated = Array.isArray(asset?.generated_images) ? asset.generated_images : [];
              generated.forEach((img) => {
                const key = normalizeUrlForMatch(img?.url);
                if (key) {
                  existingByGeneratedOverlayUrl.set(key, asset);
                }
              });
            });
            const seededAssets = Array.from(groupedByOriginal.values()).map((group, idx) => {
              const matchingGeneratedAsset = (group.generated || [])
                .map((img) => normalizeUrlForMatch(img?.url))
                .filter(Boolean)
                .map((key) => existingByGeneratedOverlayUrl.get(key))
                .find(Boolean);
              const existing =
                existingByHash.get(group.key) ||
                existingByNormalizedUrl.get(normalizeUrlForMatch(group.original)) ||
                matchingGeneratedAsset;
              const existingGenerated = Array.isArray(existing?.generated_images) ? existing.generated_images : [];
              const byUrl = new Map();
              group.generated
                .filter((img) => img?.url)
                .forEach((img) => {
                  const key = normalizeUrlForMatch(img.url);
                  if (!key) return;
                  byUrl.set(key, img);
                });
              existingGenerated
                .filter((img) => img?.url)
                .forEach((img) => {
                  const key = normalizeUrlForMatch(img.url);
                  if (!key) return;
                  const currentGenerated = byUrl.get(key);
                  byUrl.set(key, mergeGeneratedImageMeta(currentGenerated, img));
                });
              return {
                id: existing?.id || `seed-${normalizedUserId}-${idx + 1}`,
                name: existing?.name || `Lifestyle Asset ${idx + 1}`,
                data_url: group.original,
                source_gallery_id: existing?.source_gallery_id || group.gallery_id || null,
                source_lifestyle_hash: group.key || existing?.source_lifestyle_hash || null,
                created_at: existing?.created_at || new Date().toISOString(),
                generated_images: Array.from(byUrl.values()),
              };
            });
            // Keep manually uploaded assets too, if they are not represented by gallery originals.
            const seededHashSet = new Set(
              seededAssets
                .map((asset) => asset?.source_lifestyle_hash)
                .filter(Boolean),
            );
            const seededUrlSet = new Set(
              seededAssets
                .map((asset) => normalizeUrlForMatch(asset?.data_url))
                .filter(Boolean),
            );
            const remainingManual = currentAssets.filter(
              (asset) =>
                asset?.data_url &&
                !seededHashSet.has(asset?.source_lifestyle_hash) &&
                !seededUrlSet.has(normalizeUrlForMatch(asset.data_url)),
            );
            const mergedAssets = [...seededAssets, ...remainingManual];
            const dedupedList = [];
            const mergeAssetRecords = (current, incoming) => {
              const currentGenerated = Array.isArray(current?.generated_images) ? current.generated_images : [];
              const incomingGenerated = Array.isArray(incoming?.generated_images) ? incoming.generated_images : [];
              const generatedByUrl = new Map();
              [...currentGenerated, ...incomingGenerated]
                .filter((img) => img?.url)
                .forEach((img) => {
                  const key = normalizeUrlForMatch(img.url);
                  if (!key) return;
                  const existingGenerated = generatedByUrl.get(key);
                  generatedByUrl.set(key, mergeGeneratedImageMeta(existingGenerated, img));
                });
              const currentHasCustomName = Boolean(current?.name && !/^Lifestyle Asset \d+$/i.test(current.name));
              const incomingHasCustomName = Boolean(incoming?.name && !/^Lifestyle Asset \d+$/i.test(incoming.name));
              const currentUrl = current?.data_url;
              const incomingUrl = incoming?.data_url;
              const chosenDataUrl =
                !isDataUrl(currentUrl) && currentUrl
                  ? currentUrl
                  : !isDataUrl(incomingUrl) && incomingUrl
                  ? incomingUrl
                  : currentUrl || incomingUrl;
              return {
                ...current,
                ...incoming,
                id: current?.id || incoming?.id,
                name: incomingHasCustomName
                  ? incoming.name
                  : currentHasCustomName
                  ? current.name
                  : current?.name || incoming?.name,
                data_url: chosenDataUrl,
                source_gallery_id: current?.source_gallery_id || incoming?.source_gallery_id || null,
                source_lifestyle_hash: current?.source_lifestyle_hash || incoming?.source_lifestyle_hash || null,
                generated_images: Array.from(generatedByUrl.values()),
              };
            };

            mergedAssets.forEach((asset, idx) => {
              if (!asset) return;
              const assetHash = asset.source_lifestyle_hash || null;
              const assetUrl = normalizeUrlForMatch(asset.data_url);
              const assetGeneratedKeys = new Set(
                (Array.isArray(asset.generated_images) ? asset.generated_images : [])
                  .map((img) => normalizeUrlForMatch(img?.url))
                  .filter(Boolean),
              );

              const existingIndex = dedupedList.findIndex((entry) => {
                const sameHash = assetHash && entry?.source_lifestyle_hash && assetHash === entry.source_lifestyle_hash;
                const sameUrl = assetUrl && normalizeUrlForMatch(entry?.data_url) === assetUrl;
                const entryGeneratedKeys = new Set(
                  (Array.isArray(entry?.generated_images) ? entry.generated_images : [])
                    .map((img) => normalizeUrlForMatch(img?.url))
                    .filter(Boolean),
                );
                const hasSharedGenerated = Array.from(assetGeneratedKeys).some((key) => entryGeneratedKeys.has(key));
                return sameHash || sameUrl || hasSharedGenerated;
              });

              if (existingIndex === -1) {
                dedupedList.push({ ...asset, __order: idx });
              } else {
                dedupedList[existingIndex] = mergeAssetRecords(dedupedList[existingIndex], asset);
              }
            });

            return dedupedList.slice(0, 60).map(({ __order, ...rest }) => rest);
          });
        }
      }
      const latest = items[0];
      if (latest) {
        // no-op; latest preview is available in galleryResults
      }
    } catch (error) {
      message.error(error.message || 'Unable to load user gallery images');
    } finally {
      setGalleryLoading(false);
    }
  }, [normalizedUserId, updateCurrentUserAssets]);

  useEffect(() => {
    refreshGalleryResults();
  }, [refreshGalleryResults]);

  useEffect(() => {
    const activeJobIds = Object.entries(jobMap)
      .filter(([, value]) => value?.status === 'queued' || value?.status === 'running')
      .map(([jobId]) => jobId);
    if (!activeJobIds.length) {
      return undefined;
    }
    const interval = setInterval(async () => {
      const updates = await Promise.all(
        activeJobIds.map((jobId) =>
          getTryOnJobStatus(jobId)
            .then((data) => ({ jobId, data }))
            .catch(() => null),
        ),
      );
      let hasCompletion = false;
      setJobMap((prev) => {
        const next = { ...prev };
        updates.forEach((entry) => {
          if (!entry?.data) return;
          next[entry.jobId] = {
            ...next[entry.jobId],
            status: entry.data.status || next[entry.jobId]?.status,
            message: entry.data.message || next[entry.jobId]?.message,
            progress: entry.data.progress ?? next[entry.jobId]?.progress,
            duration_ms: next[entry.jobId]?.duration_ms,
          };
          if (entry.data.status === 'completed') {
            hasCompletion = true;
            const preview = toAbsoluteUrl(entry.data.overlay_url || entry.data.preview_url || entry.data.original_url);
            if (preview) {
              const linkedAssetId = next[entry.jobId]?.assetId;
              if (linkedAssetId) {
                const swatchName = next[entry.jobId]?.swatchName || entry.data.swatch_name || '';
                const swatchId = next[entry.jobId]?.swatchId || entry.data.swatch_id || null;
                const durationMs = next[entry.jobId]?.request_sent_at
                  ? Date.now() - next[entry.jobId].request_sent_at
                  : null;
                next[entry.jobId] = {
                  ...next[entry.jobId],
                  duration_ms: durationMs,
                };
                updateCurrentUserAssets((assets) =>
                  assets.map((asset) => {
                    if (asset.id !== linkedAssetId) {
                      return asset;
                    }
                    const existing = Array.isArray(asset.generated_images) ? asset.generated_images : [];
                    const existingIndex = existing.findIndex((item) => item.url === preview);
                    if (existingIndex >= 0) {
                      const updated = [...existing];
                      updated[existingIndex] = mergeGeneratedImageMeta(updated[existingIndex], {
                        url: preview,
                        swatch_name: swatchName,
                        swatch_id: swatchId,
                        duration_ms: durationMs,
                        local_only: true,
                        created_at: new Date().toISOString(),
                      });
                      return { ...asset, generated_images: updated };
                    }
                    return {
                      ...asset,
                      generated_images: [
                        {
                          url: preview,
                          swatch_name: swatchName,
                          swatch_id: swatchId,
                          duration_ms: durationMs,
                          local_only: true,
                          created_at: new Date().toISOString(),
                        },
                        ...existing,
                      ],
                    };
                  }),
                );
              }
            }
          }
        });
        return next;
      });
      if (hasCompletion) {
        setSystemMessage({ type: 'success', text: 'Your tryout is ready in gallery results.' });
        notification.success({
          message: 'Try This Look completed',
          description: 'Your generated image is ready in My Gallery.',
          placement: 'topRight',
          duration: 4,
        });
        refreshGalleryResults();
      }
    }, 3500);
    return () => clearInterval(interval);
  }, [jobMap, refreshGalleryResults, updateCurrentUserAssets]);

  const onSelectFile = useCallback(async (file) => {
    if (!file) return;
    if (pendingPreview) {
      URL.revokeObjectURL(pendingPreview);
    }
    setPendingFile(file);
    setPendingPreview(URL.createObjectURL(file));
    if (!assetName.trim()) {
      setAssetName(file.name.replace(/\.[^.]+$/, ''));
    }
    setAssetSaveModalOpen(true);
  }, [assetName, pendingPreview]);

  const stopCameraStream = useCallback(() => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
  }, []);

  const openCameraModal = useCallback(async () => {
    if (!normalizedUserId) {
      message.warning('Set and save user ID first');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      message.info('Direct camera capture is not available. Opening device camera upload.');
      cameraInputRef.current?.click();
      return;
    }
    setCameraModalOpen(true);
    setCameraLoading(true);
    try {
      stopCameraStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      cameraStreamRef.current = stream;
    } catch (error) {
      message.error('Unable to access camera. Using device file picker instead.');
      setCameraModalOpen(false);
      cameraInputRef.current?.click();
    } finally {
      setCameraLoading(false);
    }
  }, [normalizedUserId, stopCameraStream]);

  const closeCameraModal = useCallback(() => {
    setCameraModalOpen(false);
    setCameraLoading(false);
    stopCameraStream();
  }, [stopCameraStream]);

  const captureFromCamera = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) {
      return;
    }
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      message.error('Unable to capture image from camera');
      return;
    }
    context.drawImage(video, 0, 0, width, height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          message.error('Unable to capture image from camera');
          return;
        }
        const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
        onSelectFile(file);
        closeCameraModal();
      },
      'image/jpeg',
      0.95,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeCameraModal]);

  const handlePreviewTouchStart = useCallback(
    (event) => {
      if (event.touches?.length === 2) {
        const [first, second] = event.touches;
        const distance = Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
        pinchStateRef.current = {
          distance,
          zoom: previewZoom,
        };
        panStateRef.current.mode = 'pinch';
        return;
      }
      if (event.touches?.length === 1 && previewZoom > 1) {
        const [touch] = event.touches;
        panStateRef.current = {
          startX: touch.clientX,
          startY: touch.clientY,
          startOffsetX: previewOffset.x,
          startOffsetY: previewOffset.y,
          mode: 'pan',
        };
        setIsPreviewDragging(true);
        return;
      }
      panStateRef.current.mode = null;
    },
    [previewOffset.x, previewOffset.y, previewZoom],
  );

  const handlePreviewTouchMove = useCallback((event) => {
    if (panStateRef.current.mode === 'pinch') {
      if (event.touches?.length !== 2 || !pinchStateRef.current.distance) {
        return;
      }
      event.preventDefault();
      const [first, second] = event.touches;
      const distance = Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
      const scale = distance / pinchStateRef.current.distance;
      const nextZoom = Math.max(0.4, Math.min(3, pinchStateRef.current.zoom * scale));
      setPreviewZoom(Number(nextZoom.toFixed(2)));
      return;
    }
    if (panStateRef.current.mode === 'pan' && event.touches?.length === 1 && previewZoom > 1) {
      event.preventDefault();
      const [touch] = event.touches;
      const dx = touch.clientX - panStateRef.current.startX;
      const dy = touch.clientY - panStateRef.current.startY;
      setPreviewOffset({
        x: panStateRef.current.startOffsetX + dx,
        y: panStateRef.current.startOffsetY + dy,
      });
    }
  }, [previewZoom]);

  const handlePreviewTouchEnd = useCallback(() => {
    pinchStateRef.current = { distance: 0, zoom: previewZoom };
    panStateRef.current.mode = null;
    setIsPreviewDragging(false);
  }, [previewZoom]);

  const handlePreviewMouseDown = useCallback(
    (event) => {
      if (previewZoom <= 1) return;
      event.preventDefault();
      panStateRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        startOffsetX: previewOffset.x,
        startOffsetY: previewOffset.y,
        mode: 'pan-mouse',
      };
      setIsPreviewDragging(true);
    },
    [previewOffset.x, previewOffset.y, previewZoom],
  );

  const handlePreviewWheel = useCallback((event) => {
    // Trackpad pinch in desktop browsers usually comes through as wheel + ctrlKey.
    if (!event.ctrlKey) {
      return;
    }
    event.preventDefault();
    const delta = -event.deltaY;
    const step = delta > 0 ? 0.08 : -0.08;
    setPreviewZoom((current) => {
      const next = Math.max(0.4, Math.min(3, current + step));
      return Number(next.toFixed(2));
    });
  }, []);

  useEffect(() => {
    if (!isPreviewDragging || panStateRef.current.mode !== 'pan-mouse') {
      return undefined;
    }
    const onMove = (event) => {
      if (previewZoom <= 1) return;
      const dx = event.clientX - panStateRef.current.startX;
      const dy = event.clientY - panStateRef.current.startY;
      setPreviewOffset({
        x: panStateRef.current.startOffsetX + dx,
        y: panStateRef.current.startOffsetY + dy,
      });
    };
    const onUp = () => {
      panStateRef.current.mode = null;
      setIsPreviewDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isPreviewDragging, previewZoom]);

  useEffect(() => {
    if (previewZoom <= 1) {
      setPreviewOffset({ x: 0, y: 0 });
    }
  }, [previewZoom]);

  useEffect(() => {
    if (!previewModalOpen) {
      return undefined;
    }
    const preventGesture = (event) => {
      event.preventDefault();
    };
    const preventPagePinch = (event) => {
      if (event.touches?.length >= 2) {
        event.preventDefault();
      }
    };

    document.addEventListener('gesturestart', preventGesture, { passive: false });
    document.addEventListener('gesturechange', preventGesture, { passive: false });
    document.addEventListener('gestureend', preventGesture, { passive: false });
    document.addEventListener('touchmove', preventPagePinch, { passive: false });

    return () => {
      document.removeEventListener('gesturestart', preventGesture);
      document.removeEventListener('gesturechange', preventGesture);
      document.removeEventListener('gestureend', preventGesture);
      document.removeEventListener('touchmove', preventPagePinch);
    };
  }, [previewModalOpen]);

  useEffect(() => {
    if (!cameraModalOpen || !cameraStreamRef.current || !videoRef.current) {
      return;
    }
    const video = videoRef.current;
    video.srcObject = cameraStreamRef.current;
    video
      .play()
      .catch(() => {
        // If autoplay is blocked, user can still capture after interacting.
      });
  }, [cameraModalOpen, cameraLoading]);

  const saveUserId = () => {
    const value = userIdInput.trim();
    if (!value) {
      message.warning('User ID is required');
      return;
    }
    setSavedUserId(value);
    setEditingUserId(false);
    setSelectedAssetId(null);
    try {
      localStorage.setItem(userIdStorageKey, value);
    } catch (error) {
      // ignore storage failures
    }
    setSystemMessage({ type: 'success', text: `User ID "${value}" saved. Gallery is now user-scoped.` });
  };

  const saveAsset = async () => {
    if (!normalizedUserId) {
      message.warning('Set and save user ID first');
      return;
    }
    if (!pendingFile) {
      message.warning('Upload or capture an image first');
      return;
    }
    if (!assetName.trim()) {
      message.warning('Asset name is required');
      return;
    }
    setSavingAsset(true);
    try {
      const dataUrl = await fileToDataUrl(pendingFile);
      const nextAsset = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: assetName.trim(),
        data_url: dataUrl,
        created_at: new Date().toISOString(),
        generated_images: [],
      };
      updateCurrentUserAssets((currentAssets) => [nextAsset, ...currentAssets].slice(0, 60));
      setSelectedAssetId(nextAsset.id);
      setAssetName('');
      setPendingFile(null);
      if (pendingPreview) {
        URL.revokeObjectURL(pendingPreview);
      }
      setPendingPreview(null);
      setAssetSaveModalOpen(false);
      setSystemMessage({ type: 'success', text: 'Asset added to My Gallery. Pick a swatch and tap "Try This Look".' });
    } catch (error) {
      message.error(error.message || 'Unable to save asset');
    } finally {
      setSavingAsset(false);
    }
  };

  const closeAssetSaveModal = useCallback(() => {
    setAssetSaveModalOpen(false);
    setPendingFile(null);
    if (pendingPreview) {
      URL.revokeObjectURL(pendingPreview);
    }
    setPendingPreview(null);
    setAssetName('');
  }, [pendingPreview]);

  const selectedAsset = useMemo(() => userAssets.find((item) => item.id === selectedAssetId) || null, [selectedAssetId, userAssets]);
  const generatedForSelectedAsset = useMemo(() => {
    if (!selectedAsset) return [];
    const fromGallery = galleryResults.filter((entry) => {
      const byHash =
        selectedAsset.source_lifestyle_hash &&
        entry?.lifestyle_hash &&
        selectedAsset.source_lifestyle_hash === entry.lifestyle_hash;
      const byOriginalUrl =
        normalizeUrlForMatch(entry?.original_url) === normalizeUrlForMatch(selectedAsset?.data_url);
      return byHash || byOriginalUrl;
    });
    const byNormalizedOverlay = new Map(
      fromGallery
        .map((entry) => [normalizeUrlForMatch(entry.overlay_url || entry.original_url), entry])
        .filter(([key]) => key),
    );
    const localGenerated = Array.isArray(selectedAsset.generated_images) ? selectedAsset.generated_images : [];
    const localOnly = localGenerated
      .filter((entry) => entry?.url)
      .filter((entry) => !byNormalizedOverlay.has(normalizeUrlForMatch(entry.url)))
      .map((entry, idx) => ({
        id: `local-${selectedAsset.id}-${idx + 1}`,
        overlay_url: entry.url,
        original_url: selectedAsset.data_url,
        swatch_name: entry.swatch_name,
        swatch_id: entry.swatch_id,
        duration_ms: entry.duration_ms,
        updated_at: entry.created_at,
        __localOnly: true,
      }));

    return [...localOnly, ...fromGallery];
  }, [galleryResults, selectedAsset]);

  useEffect(() => {
    if (!normalizedUserId) {
      setSelectedAssetId(null);
      return;
    }
    if (!userAssets.length) {
      setSelectedAssetId(null);
      return;
    }
    const selectedStillExists = userAssets.some((asset) => asset.id === selectedAssetId);
    if (!selectedStillExists) {
      // Default to most recently uploaded asset (stored at index 0).
      setSelectedAssetId(userAssets[0].id);
    }
  }, [normalizedUserId, selectedAssetId, userAssets]);

  const handleTryThisLook = async (swatch) => {
    if (!normalizedUserId) {
      message.warning('Set and save user ID first');
      setEditingUserId(true);
      return;
    }
    if (!selectedAsset) {
      message.warning('Select an asset from My Gallery first');
      return;
    }
    setSubmittingSwatchId(swatch.id);
    setSystemMessage({ type: 'info', text: `Submitting tryout for ${swatch.name}...` });
    try {
      const requestSentAt = Date.now();
      const response = selectedAsset.source_gallery_id
        ? await createTryOnJobFromGallery({
            galleryId: selectedAsset.source_gallery_id,
            swatchId: swatch.id,
            userId: normalizedUserId,
          })
        : await createTryOnJob({
            environmentFile: await dataUrlToFile(selectedAsset.data_url, `${selectedAsset.name || 'asset'}.jpg`),
            swatchId: swatch.id,
            userId: normalizedUserId,
          });
      const jobId = response?.job_id || response?.jobId;
      if (jobId) {
        setJobMap((prev) => ({
          ...prev,
          [jobId]: {
            swatchName: swatch.name,
            swatchId: swatch.id,
            assetId: selectedAssetId,
            request_sent_at: requestSentAt,
            status: response?.status || 'queued',
            message: response?.message || 'Queued',
            progress: response?.progress ?? 0,
          },
        }));
      }
      setSystemMessage({ type: 'info', text: `Tryout queued for "${swatch.name}". Running in background...` });
      message.success('Try This Look started');
    } catch (error) {
      if (error?.code === 'tryon_exists') {
        const existing = error?.payload?.existing || {};
        const existingOverlay = toAbsoluteUrl(existing.overlay_url || existing.preview_url || existing.original_url);
        setExistingTryOnData({
          swatch,
          selectedAsset,
          existing,
          existingOverlay,
        });
        setExistingTryOnModalOpen(true);
        setSystemMessage({
          type: 'warning',
          text: 'A try-on already exists for this user, swatch, and lifestyle image.',
        });
        return;
      }
      setSystemMessage({ type: 'error', text: error.message || 'Unable to queue tryout' });
      message.error(error.message || 'Unable to queue tryout');
    } finally {
      setSubmittingSwatchId(null);
    }
  };

  const handleReapplyExistingTryOn = useCallback(async () => {
    if (!existingTryOnData?.swatch || !existingTryOnData?.selectedAsset || !normalizedUserId) {
      return;
    }
    setReapplyLoading(true);
    try {
      const requestSentAt = Date.now();
      const response = existingTryOnData.selectedAsset?.source_gallery_id
        ? await createTryOnJobFromGallery({
            galleryId: existingTryOnData.selectedAsset.source_gallery_id,
            swatchId: existingTryOnData.swatch.id,
            userId: normalizedUserId,
            forceOverwrite: true,
          })
        : await createTryOnJob({
            environmentFile: await dataUrlToFile(
              existingTryOnData.selectedAsset.data_url,
              `${existingTryOnData.selectedAsset.name || 'asset'}.jpg`,
            ),
            swatchId: existingTryOnData.swatch.id,
            userId: normalizedUserId,
            forceOverwrite: true,
          });
      const jobId = response?.job_id || response?.jobId;
      if (jobId) {
        setJobMap((prev) => ({
          ...prev,
          [jobId]: {
            swatchName: existingTryOnData.swatch.name,
            swatchId: existingTryOnData.swatch.id,
            assetId: existingTryOnData.selectedAsset.id,
            request_sent_at: requestSentAt,
            status: response?.status || 'queued',
            message: response?.message || 'Queued',
            progress: response?.progress ?? 0,
          },
        }));
      }
      setExistingTryOnModalOpen(false);
      setExistingTryOnData(null);
      setSystemMessage({
        type: 'info',
        text: `Reapply queued for "${existingTryOnData.swatch.name}". Running in background...`,
      });
      message.success('Reapply queued');
    } catch (error) {
      message.error(error.message || 'Unable to reapply try-on');
    } finally {
      setReapplyLoading(false);
    }
  }, [existingTryOnData, normalizedUserId]);

  const handleReviewExistingTryOn = useCallback(() => {
    if (!existingTryOnData?.existingOverlay) {
      message.info('No generated preview found for existing try-on');
      return;
    }
    setPreviewTitle(
      existingTryOnData?.swatch?.name
        ? `${existingTryOnData.swatch.name} (Existing)`
        : 'Existing try-on',
    );
    setPreviewAssetThumb(null);
    setPreviewImages([{ url: existingTryOnData.existingOverlay, label: 'Existing generated image' }]);
    setPreviewModalOpen(true);
    setExistingTryOnModalOpen(false);
  }, [existingTryOnData]);

  const removeGeneratedFromAssetsByUrl = useCallback(
    (urlToRemove) => {
      if (!urlToRemove) return;
      updateCurrentUserAssets((assets) =>
        assets.map((asset) => ({
          ...asset,
          generated_images: (Array.isArray(asset.generated_images) ? asset.generated_images : []).filter(
            (img) => img?.url !== urlToRemove,
          ),
        })),
      );
    },
    [updateCurrentUserAssets],
  );

  const confirmDeleteGeneratedImage = useCallback(
    (item) => {
      Modal.confirm({
        title: 'Delete generated image?',
        content: 'This will remove the generated image from gallery and previews.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            if (item?.id) {
              await deleteGalleryEntry(item.id);
            }
            const targetUrl = item?.overlay_url || item?.original_url;
            removeGeneratedFromAssetsByUrl(targetUrl);
            setGalleryResults((prev) => prev.filter((entry) => entry.id !== item?.id));
            message.success('Generated image deleted');
            refreshGalleryResults();
          } catch (error) {
            message.error(error.message || 'Unable to delete generated image');
          }
        },
      });
    },
    [refreshGalleryResults, removeGeneratedFromAssetsByUrl],
  );

  const confirmDeleteAsset = useCallback(
    (asset) => {
      const relatedGenerated = galleryResults.filter((entry) => {
        const byHash =
          asset?.source_lifestyle_hash &&
          entry?.lifestyle_hash &&
          asset.source_lifestyle_hash === entry.lifestyle_hash;
        const byOriginalUrl =
          normalizeUrlForMatch(entry?.original_url) === normalizeUrlForMatch(asset?.data_url);
        return byHash || byOriginalUrl;
      });
      const relatedCount = relatedGenerated.length;
      const content = relatedCount
        ? `This asset has ${relatedCount} generated image(s). Deleting asset will also delete them.`
        : 'Delete this asset?';

      Modal.confirm({
        title: 'Delete asset?',
        content,
        okText: 'Delete',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            if (relatedGenerated.length) {
              await Promise.all(
                relatedGenerated
                  .map((entry) => entry?.id)
                  .filter(Boolean)
                  .map((id) => deleteGalleryEntry(id)),
              );
            }
            updateCurrentUserAssets((assets) => assets.filter((entry) => entry.id !== asset.id));
            if (selectedAssetId === asset.id) {
              setSelectedAssetId(null);
            }
            message.success('Asset deleted');
            refreshGalleryResults();
          } catch (error) {
            message.error(error.message || 'Unable to delete asset');
          }
        },
      });
    },
    [galleryResults, refreshGalleryResults, selectedAssetId, updateCurrentUserAssets],
  );

  const swatchListData = swatches || [];
  const isTabletGalleryCompact = viewportWidth >= 820 && viewportWidth < 992;
  const runningJobs = useMemo(
    () =>
      Object.values(jobMap).filter(
        (job) => job?.status === 'queued' || job?.status === 'running',
      ),
    [jobMap],
  );
  const showTopSystemMessage = showModuleNav && Boolean(systemMessage?.text);
  const listGrid =
    viewMode === 'grid'
      ? {
          gutter: 16,
          xs: 1,
          sm: 2,
          md: 2,
          lg: 3,
          xl: 4,
          xxl: 4,
        }
      : undefined;

  return (
    <div className="ai-image-masking-page">
      {showModuleNav && <ModuleNav activeKey="new-ux" />}

      {showTopSystemMessage && (
        <Alert
          showIcon
          className="mb-3"
          type={systemMessage.type}
          message={systemMessage.text}
        />
      )}

      <Card className="shadow-sm mb-3">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} lg={8}>
            <Title level={4} className="mb-0 ai-visualizer-header-title">
              AI Visualizer
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Headless by design, with rapid plug-and-play integrations for any platform.
            </Text>
          </Col>
          <Col xs={24} lg={8}>
            <div className="d-flex justify-content-center">
              <Input.Search
                allowClear
                enterButton="Search"
                size="large"
                placeholder="Search swatches"
                prefix={<SearchOutlined />}
                onSearch={(value) => {
                  setSearchTerm((value || '').trim());
                  setSwatchPage(1);
                }}
                style={{ width: '100%', maxWidth: 520 }}
              />
            </div>
          </Col>
          <Col xs={24} lg={8}>
            <div className="d-flex justify-content-md-end">
              <Space wrap align="center">
                {editingUserId ? (
                  <>
                    <div style={{ width: 260, maxWidth: '100%' }}>
                      <Input
                        placeholder="Set User ID"
                        value={userIdInput}
                        onChange={(e) => setUserIdInput(e.target.value)}
                        style={{ width: '100%' }}
                      />
                      {!normalizedUserId && !userIdInput.trim() && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {USER_ID_PROMPT_TEXT}
                        </Text>
                      )}
                    </div>
                    <Button type="primary" icon={<SaveOutlined />} onClick={saveUserId}>
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <Tag color="blue" style={{ margin: 0 }}>
                      User ID: {normalizedUserId || 'Not set'}
                    </Tag>
                    <Button onClick={() => setEditingUserId(true)}>Change</Button>
                  </>
                )}
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]} align="top">
        <Col xs={24} md={14} lg={18}>
          <Card className="shadow-sm" bodyStyle={{ padding: 16 }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div className="ms-auto" style={{ width: '100%', maxWidth: 260 }}>
                <Segmented
                  block
                  value={viewMode}
                  onChange={setViewMode}
                  options={[
                    { label: 'Grid', value: 'grid' },
                    { label: 'Rows', value: 'rows' },
                  ]}
                />
              </div>
            </div>
            {runningJobs.length > 0 && (
              <Alert
                showIcon
                type="info"
                className="mb-3"
                message={`Try This Look in progress (${runningJobs.length})`}
                description="Your image is being generated in the background."
              />
            )}
            <Spin spinning={swatchLoading}>
              <List
                dataSource={swatchListData}
                grid={listGrid}
                locale={{ emptyText: 'No swatches found' }}
                renderItem={(swatch) => (
                  <List.Item key={swatch.id} style={viewMode === 'grid' ? { width: '100%' } : undefined}>
                    {viewMode === 'grid' ? (
                      <Card
                        hoverable
                        size="small"
                        bodyStyle={{ padding: 12, height: '100%' }}
                        style={{ height: '100%', width: '100%', minWidth: 0 }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 280, gap: 8 }}>
                          <div className="d-flex justify-content-center">
                            <div
                              style={{
                                width: 180,
                                minWidth: 180,
                                height: 120,
                                overflow: 'hidden',
                                borderRadius: 8,
                                background: '#f5f5f5',
                              }}
                            >
                              <Image
                                src={toAbsoluteUrl(swatch.blob_url)}
                                alt={swatch.name}
                                preview={false}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </div>
                          </div>
                          <div>
                            <Text strong>{swatch.name}</Text>
                            <Paragraph
                              type="secondary"
                              className="mb-0"
                              style={{
                                fontSize: 12,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                              }}
                            >
                              {swatch.description || 'No description'}
                            </Paragraph>
                          </div>
                          <Space size={[4, 4]} wrap>
                            {(swatch.tags || []).slice(0, 4).map((tag) => (
                              <Tag key={`${swatch.id}-${tag}`}>{tag}</Tag>
                            ))}
                          </Space>
                          <div style={{ marginTop: 'auto' }}>
                            <Button
                              type="primary"
                              icon={<UploadOutlined />}
                              onClick={() => handleTryThisLook(swatch)}
                              loading={submittingSwatchId === swatch.id}
                              disabled={!selectedAsset || !normalizedUserId}
                              block
                            >
                              Try This Look
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ) : (
                      <Card hoverable size="small" bodyStyle={{ padding: 12 }} style={{ height: '100%' }}>
                        <div className="ai-visualizer-row-card d-flex gap-3 align-items-stretch" style={{ minHeight: 160 }}>
                          <div
                            className="ai-visualizer-row-thumb"
                            style={{
                              width: 180,
                              minWidth: 180,
                              height: 120,
                              overflow: 'hidden',
                              borderRadius: 8,
                              background: '#f5f5f5',
                            }}
                          >
                            <Image
                              src={toAbsoluteUrl(swatch.blob_url)}
                              alt={swatch.name}
                              preview={false}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </div>
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div>
                              <Text strong>{swatch.name}</Text>
                              <Paragraph type="secondary" className="mb-0" style={{ fontSize: 12 }}>
                                {swatch.description || 'No description'}
                              </Paragraph>
                            </div>
                            <Space size={[4, 4]} wrap>
                              {(swatch.tags || []).slice(0, 6).map((tag) => (
                                <Tag key={`${swatch.id}-${tag}`}>{tag}</Tag>
                              ))}
                            </Space>
                            <div style={{ marginTop: 'auto' }}>
                              <Button
                                type="primary"
                                icon={<UploadOutlined />}
                                onClick={() => handleTryThisLook(swatch)}
                                loading={submittingSwatchId === swatch.id}
                                disabled={!selectedAsset || !normalizedUserId}
                              >
                                Try This Look
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    )}
                  </List.Item>
                )}
              />

              {swatchTotal > SWATCH_PAGE_SIZE && (
                <div className="d-flex justify-content-end mt-3 flex-wrap gap-2">
                  <Button disabled={swatchPage <= 1} onClick={() => setSwatchPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Text className="mx-1">
                    Page {swatchPage}
                  </Text>
                  <Button
                    disabled={swatchPage * SWATCH_PAGE_SIZE >= swatchTotal}
                    onClick={() => setSwatchPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </Spin>
          </Card>
        </Col>

        <Col xs={24} md={10} lg={6}>
          <Card
            className="shadow-sm"
            title="My Gallery"
            style={{ position: 'sticky', top: 12 }}
            bodyStyle={{ padding: 12, maxHeight: 'calc(100vh - 230px)', overflowY: 'auto' }}
          >
            {userAssets.length === 0 && !pendingPreview && (
              <Alert
                showIcon
                type="info"
                className="mb-2"
                message='Upload an image to "Try This Look"'
              />
            )}
            <Space direction="vertical" className="w-100" size="small">
              <div className="d-flex flex-column flex-sm-row gap-2">
                <Button
                  icon={<PictureOutlined />}
                  onClick={() => galleryInputRef.current?.click()}
                  disabled={!normalizedUserId}
                  style={{ flex: 1, minWidth: 0, height: 40 }}
                >
                  Upload
                </Button>
                <Button
                  icon={<CameraOutlined />}
                  onClick={openCameraModal}
                  disabled={!normalizedUserId}
                  style={{ flex: 1, minWidth: 0, height: 40 }}
                >
                  Camera
                </Button>
              </div>
            </Space>

            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(event) => {
                onSelectFile(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={(event) => {
                onSelectFile(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            <div className="mt-3">
              <Text strong>Assets ({userAssets.length})</Text>
              <List
                className="mt-2"
                size="small"
                dataSource={userAssets}
                locale={{ emptyText: normalizedUserId ? 'No assets yet' : 'Set user ID first' }}
                renderItem={(asset) => (
                  <List.Item
                    style={{
                      cursor: 'pointer',
                      border: selectedAssetId === asset.id ? '1px solid #1677ff' : '1px solid #f0f0f0',
                      borderRadius: 8,
                      marginBottom: 8,
                      padding: 8,
                      position: 'relative',
                      overflow: 'hidden',
                      alignItems: 'stretch',
                    }}
                    onClick={() => setSelectedAssetId(asset.id)}
                  >
                    <div
                      style={{
                        position: isTabletGalleryCompact ? 'static' : 'absolute',
                        top: isTabletGalleryCompact ? 'auto' : 8,
                        right: isTabletGalleryCompact ? 'auto' : 8,
                        background: 'linear-gradient(135deg, #1677ff 0%, #0958d9 100%)',
                        color: '#fff',
                        borderRadius: 999,
                        padding: '2px 10px',
                        fontSize: 11,
                        fontWeight: 600,
                        boxShadow: '0 4px 10px rgba(22, 119, 255, 0.25)',
                        zIndex: 1,
                        alignSelf: isTabletGalleryCompact ? 'flex-start' : 'auto',
                        marginBottom: isTabletGalleryCompact ? 8 : 0,
                      }}
                    >
                      Tryouts {Array.isArray(asset.generated_images) ? asset.generated_images.length : 0}
                    </div>
                    <Space align="start" size={10} style={{ width: '100%' }}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={(event) => {
                          event.stopPropagation();
                          const generated = Array.isArray(asset.generated_images) ? asset.generated_images : [];
                          const images = generated
                            .filter((item) => item.url)
                            .map((item) => ({
                              url: item.url,
                              label:
                                `${item.swatch_name || `Swatch #${item.swatch_id || ''}`}` +
                                `${formatDuration(item.duration_ms) ? ` - ${formatDuration(item.duration_ms)}` : ''}`,
                            }));
                          setPreviewTitle(`${asset.name} - Asset Preview`);
                          setPreviewAssetThumb(asset.data_url);
                          setPreviewImages(images);
                          setPreviewModalOpen(true);
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return;
                          event.preventDefault();
                          event.stopPropagation();
                          const generated = Array.isArray(asset.generated_images) ? asset.generated_images : [];
                          const images = generated
                            .filter((item) => item.url)
                            .map((item) => ({
                              url: item.url,
                              label:
                                `${item.swatch_name || `Swatch #${item.swatch_id || ''}`}` +
                                `${formatDuration(item.duration_ms) ? ` - ${formatDuration(item.duration_ms)}` : ''}`,
                            }));
                          setPreviewTitle(`${asset.name} - Asset Preview`);
                          setPreviewAssetThumb(asset.data_url);
                          setPreviewImages(images);
                          setPreviewModalOpen(true);
                        }}
                        style={{ cursor: 'pointer' }}
                        aria-label={`Preview ${asset.name}`}
                      >
                        <Image
                          src={asset.data_url}
                          alt={asset.name}
                          width={isTabletGalleryCompact ? 110 : 150}
                          height={isTabletGalleryCompact ? 110 : 150}
                          preview={false}
                          style={{ objectFit: 'cover', borderRadius: 6 }}
                        />
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <Text strong>{asset.name}</Text>
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {new Date(asset.created_at).toLocaleString()}
                          </Text>
                        </div>
                        <Space wrap size={6} className="mt-1">
                          <Button
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              const generated = Array.isArray(asset.generated_images) ? asset.generated_images : [];
                              const images = generated
                                .filter((item) => item.url)
                                .map((item) => ({
                                  url: item.url,
                                  label:
                                    `${item.swatch_name || `Swatch #${item.swatch_id || ''}`}` +
                                    `${formatDuration(item.duration_ms) ? ` - ${formatDuration(item.duration_ms)}` : ''}`,
                                }));
                              setPreviewTitle(`${asset.name} - Asset Preview`);
                              setPreviewAssetThumb(asset.data_url);
                              setPreviewImages(images);
                              setPreviewModalOpen(true);
                            }}
                          >
                            Preview
                          </Button>
                          <Button
                            size="small"
                            danger
                            onClick={(event) => {
                              event.stopPropagation();
                              confirmDeleteAsset(asset);
                            }}
                          >
                            Delete
                          </Button>
                        </Space>
                      </div>
                    </Space>
                    {selectedAssetId === asset.id && <CheckCircleOutlined style={{ color: '#1677ff' }} />}
                  </List.Item>
                )}
              />
            </div>

            <div className="mt-3">
              <Text strong>
                Generated For {selectedAsset ? `"${selectedAsset.name}"` : 'Selected Asset'}
              </Text>
              {galleryLoading ? (
                <div className="py-3 text-center"><Spin /></div>
              ) : !selectedAsset ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select an asset to view generated images" />
              ) : !generatedForSelectedAsset.length ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No generated images for this asset yet" />
              ) : (
                <List
                  className="mt-2"
                  size="small"
                  dataSource={generatedForSelectedAsset}
                  renderItem={(item) => (
                    <List.Item>
                      <div className="d-flex align-items-start flex-wrap gap-2 w-100" style={{ minWidth: 0 }}>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            const single = item.overlay_url || item.original_url;
                            if (!single) return;
                            const computedDuration = item.duration_ms ?? generatedDurationByUrl[single];
                            setPreviewTitle(item.swatch_name || `Swatch #${item.swatch_id}`);
                            setPreviewAssetThumb(null);
                            setPreviewImages([
                              {
                                url: single,
                                label:
                                  `${item.swatch_name || `Swatch #${item.swatch_id}`}` +
                                  `${formatDuration(computedDuration) ? ` - ${formatDuration(computedDuration)}` : ''}`,
                              },
                            ]);
                            setPreviewModalOpen(true);
                          }}
                          onKeyDown={(event) => {
                            if (event.key !== 'Enter' && event.key !== ' ') return;
                            event.preventDefault();
                            const single = item.overlay_url || item.original_url;
                            if (!single) return;
                            const computedDuration = item.duration_ms ?? generatedDurationByUrl[single];
                            setPreviewTitle(item.swatch_name || `Swatch #${item.swatch_id}`);
                            setPreviewAssetThumb(null);
                            setPreviewImages([
                              {
                                url: single,
                                label:
                                  `${item.swatch_name || `Swatch #${item.swatch_id}`}` +
                                  `${formatDuration(computedDuration) ? ` - ${formatDuration(computedDuration)}` : ''}`,
                              },
                            ]);
                            setPreviewModalOpen(true);
                          }}
                          style={{ cursor: 'pointer' }}
                          aria-label={`Preview ${item.swatch_name || `Swatch #${item.swatch_id}`}`}
                        >
                          <Image
                            src={item.overlay_url || item.original_url}
                            alt={item.swatch_name || 'Generated result'}
                            width={150}
                            height={150}
                            preview={false}
                            style={{ objectFit: 'cover', borderRadius: 6 }}
                          />
                        </div>
                        <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <Text
                            style={{
                              fontSize: 12,
                              display: 'block',
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                              lineHeight: 1.2,
                            }}
                          >
                            {item.swatch_name || `Swatch #${item.swatch_id}`}
                          </Text>
                          {formatDuration(item.duration_ms ?? generatedDurationByUrl[item.overlay_url || item.original_url]) && (
                            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                              {formatDuration(item.duration_ms ?? generatedDurationByUrl[item.overlay_url || item.original_url])}
                            </Text>
                          )}
                          <Space wrap size={8}>
                            <Button
                              size="small"
                              onClick={() => {
                                const single = item.overlay_url || item.original_url;
                                if (!single) return;
                                const computedDuration = item.duration_ms ?? generatedDurationByUrl[single];
                                setPreviewTitle(item.swatch_name || `Swatch #${item.swatch_id}`);
                                setPreviewAssetThumb(null);
                                setPreviewImages([
                                  {
                                    url: single,
                                    label:
                                      `${item.swatch_name || `Swatch #${item.swatch_id}`}` +
                                      `${formatDuration(computedDuration) ? ` - ${formatDuration(computedDuration)}` : ''}`,
                                  },
                                ]);
                                setPreviewModalOpen(true);
                              }}
                            >
                              Preview
                            </Button>
                            <Button
                              size="small"
                              danger
                              onClick={() => confirmDeleteGeneratedImage(item)}
                            >
                              Delete
                            </Button>
                          </Space>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Modal
        title="Save Asset To My Gallery"
        open={assetSaveModalOpen}
        onCancel={closeAssetSaveModal}
        footer={[
          <Button key="cancel" onClick={closeAssetSaveModal}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={saveAsset}
            loading={savingAsset}
            disabled={!normalizedUserId || !pendingFile || !assetName.trim()}
          >
            Save To My Gallery
          </Button>,
        ]}
      >
        <Space direction="vertical" className="w-100" size="middle">
          {pendingPreview && (
            <Image
              src={pendingPreview}
              alt="Pending upload"
              preview={false}
              style={{ borderRadius: 8, width: '100%', objectFit: 'cover' }}
            />
          )}
          <Input
            placeholder="Asset name"
            value={assetName}
            onChange={(e) => setAssetName(e.target.value)}
            disabled={!normalizedUserId}
          />
        </Space>
      </Modal>

      <Modal
        title={previewTitle || 'Preview'}
        open={previewModalOpen}
        onCancel={() => {
          setPreviewModalOpen(false);
          setPreviewAssetThumb(null);
          setPreviewImages([]);
          setPreviewZoom(1);
          setPreviewFlipX(false);
          setPreviewFlipY(false);
          setPreviewOffset({ x: 0, y: 0 });
          setIsPreviewDragging(false);
        }}
        footer={null}
        width={760}
      >
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button
            size="small"
            icon={<ZoomOutOutlined />}
            onClick={() => setPreviewZoom((z) => Math.max(0.4, Number((z - 0.1).toFixed(2))))}
          >
            Zoom Out
          </Button>
          <Button
            size="small"
            icon={<ZoomInOutlined />}
            onClick={() => setPreviewZoom((z) => Math.min(3, Number((z + 0.1).toFixed(2))))}
          >
            Zoom In
          </Button>
          <Button size="small" icon={<SwapOutlined />} onClick={() => setPreviewFlipX((v) => !v)}>
            Flip Horizontal
          </Button>
          <Button size="small" icon={<SwapOutlined />} onClick={() => setPreviewFlipY((v) => !v)}>
            Flip Vertical
          </Button>
          <Button
            size="small"
            onClick={() => {
              setPreviewZoom(1);
              setPreviewFlipX(false);
              setPreviewFlipY(false);
              setPreviewOffset({ x: 0, y: 0 });
            }}
          >
            Reset
          </Button>
          <Text type="secondary" style={{ fontSize: 12, alignSelf: 'center' }}>
            {Math.round(previewZoom * 100)}%
          </Text>
          <Text type="secondary" style={{ fontSize: 12, alignSelf: 'center' }}>
            Pinch to zoom on touch devices
          </Text>
        </div>
        {previewAssetThumb && (
          <Card size="small" className="mb-3" bodyStyle={{ padding: 10 }}>
            <Space align="center">
              <Image
                src={previewAssetThumb}
                alt="Asset thumbnail"
                width={72}
                height={72}
                preview={false}
                style={{ objectFit: 'cover', borderRadius: 8 }}
              />
              <div>
                <Text strong>Asset Thumbnail</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Generated images for this asset are shown below.
                  </Text>
                </div>
              </div>
            </Space>
          </Card>
        )}
        {!previewImages.length ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={previewAssetThumb ? 'No generated images found for this asset' : 'Nothing to preview'}
          />
        ) : (
          <List
            grid={{ gutter: 12, xs: 1, sm: 1, md: 2 }}
            dataSource={previewImages}
            renderItem={(item, index) => (
              <List.Item key={`${item.url}-${index}`}>
                <div style={{ width: '100%' }}>
                  <div
                    style={{
                      width: '100%',
                      overflow: 'hidden',
                      borderRadius: 10,
                      background: '#0f172a',
                      touchAction: 'none',
                      cursor: previewZoom > 1 ? (isPreviewDragging ? 'grabbing' : 'grab') : 'default',
                    }}
                    onMouseDown={handlePreviewMouseDown}
                    onWheel={handlePreviewWheel}
                    onTouchStart={handlePreviewTouchStart}
                    onTouchMove={handlePreviewTouchMove}
                    onTouchEnd={handlePreviewTouchEnd}
                    onTouchCancel={handlePreviewTouchEnd}
                  >
                    <img
                      src={item.url}
                      alt={`Preview ${index + 1}`}
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        transform: `translate(${previewOffset.x}px, ${previewOffset.y}px) scale(${previewZoom}) scaleX(${previewFlipX ? -1 : 1}) scaleY(${previewFlipY ? -1 : 1})`,
                        transformOrigin: 'center center',
                        transition: isPreviewDragging ? 'none' : 'transform 0.15s ease',
                        userSelect: 'none',
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                  {item.label && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.label}
                    </Text>
                  )}
                </div>
              </List.Item>
            )}
          />
        )}
      </Modal>

      <Modal
        title="Existing Try-On Found"
        open={existingTryOnModalOpen}
        onCancel={() => {
          setExistingTryOnModalOpen(false);
          setExistingTryOnData(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setExistingTryOnModalOpen(false);
              setExistingTryOnData(null);
            }}
          >
            Cancel
          </Button>,
          <Button key="review" onClick={handleReviewExistingTryOn}>
            Review
          </Button>,
          <Button key="reapply" type="primary" loading={reapplyLoading} onClick={handleReapplyExistingTryOn}>
            Reapply
          </Button>,
        ]}
      >
        <Space direction="vertical" className="w-100" size="middle">
          <Text>
            A try-on already exists for this user, swatch, and lifestyle image.
          </Text>
          {existingTryOnData?.existingOverlay ? (
            <Image
              src={existingTryOnData.existingOverlay}
              alt="Existing generated image"
              style={{ width: '100%', borderRadius: 8, objectFit: 'cover' }}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Generated image preview unavailable" />
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            Choose Reapply to generate again, Review to inspect existing output, or Cancel.
          </Text>
        </Space>
      </Modal>

      <Modal
        title="Take Picture"
        open={cameraModalOpen}
        onCancel={closeCameraModal}
        footer={[
          <Button key="cancel" onClick={closeCameraModal}>
            Cancel
          </Button>,
          <Button key="capture" type="primary" icon={<CameraOutlined />} onClick={captureFromCamera} disabled={cameraLoading}>
            Take Picture
          </Button>,
        ]}
      >
        <div
          style={{
            width: '100%',
            borderRadius: 12,
            overflow: 'hidden',
            background: '#111827',
            minHeight: 260,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: '100%', maxHeight: 420, objectFit: 'cover', visibility: cameraLoading ? 'hidden' : 'visible' }}
          />
          {cameraLoading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Spin />
            </div>
          )}
        </div>
      </Modal>

      <style>{`
        .ai-visualizer-header-title {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        @media (max-width: 575.98px) {
          .ai-visualizer-header-title {
            white-space: normal;
            overflow: visible;
            text-overflow: initial;
          }

          .ai-visualizer-row-card {
            flex-direction: column;
            min-height: unset !important;
          }
          .ai-visualizer-row-thumb {
            width: 100% !important;
            min-width: 0 !important;
            height: 160px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default TryoutStudioNewUX;
